import { HttpError, Offer, OfferPaginationOptions } from '@rd/types';
import { OfferActions, OfferActionTypes } from '../actions/offer.actions';

export interface OfferState {
  offerMap: {
    [key: string]: Offer;
  };
  offerList: {
    offerIds: string[];
    paginationOptions: OfferPaginationOptions;
    count: number;
    totalPageCount: number;
    loading: boolean;
    error: HttpError | null;
  };
  offerDetails: {
    offerId: string | null;
    loading: boolean;
    error: HttpError | null;
  };
}

const initialState: OfferState = {
  offerMap: {},
  offerList: {
    offerIds: [],
    paginationOptions: { page: 0, pageSize: 9 },
    count: 0,
    totalPageCount: 0,
    loading: false,
    error: null,
  },
  offerDetails: {
    offerId: null,
    loading: false,
    error: null,
  },
};

export function offerReducer(
  state: OfferState = initialState,
  action: OfferActions,
): OfferState {
  switch (action.type) {
    case OfferActionTypes.LOAD_OFFERS:
      return {
        ...state,
        offerList: {
          ...state.offerList,
          totalPageCount: 0,
          error: null,
          // only display loading state when offer list is empty e.g. first load/refetch data after error
          loading: state.offerList.offerIds.length
            ? !!action.payload.shouldShowLoadingState
            : true,
        },
      };
    case OfferActionTypes.LOAD_BNZ_HOMEPAGE_OFFERS:
      return {
        ...state,
        offerList: {
          ...state.offerList,
          error: null,
          loading: true,
        },
      };
    case OfferActionTypes.LOAD_PAGINATED_OFFERS:
      if (action.payload.queryParams?.source) {
        // when source is specified, don't reset existing offers as they may be from another source
        return {
          ...state,
          offerList: {
            ...state.offerList,
            paginationOptions: action.payload.paginationOptions,
            error: null,
            loading: true,
          },
        };
      }
      return {
        ...state,
        offerList: {
          ...initialState.offerList,
          paginationOptions: action.payload.paginationOptions,
          totalPageCount: 0,
          error: null,
          loading: true,
        },
      };
    case OfferActionTypes.LOAD_OFFERS_SUCCESS:
      if (action.payload.source) {
        // when source is specified, merge with any existing offers which may be from another source and dedupe by ID
        const existingIds = state.offerList.offerIds.filter(
          (id) => state.offerMap[id]?.source !== action.payload.source,
        );
        const dedupedOfferIds = [
          ...new Set([...existingIds, ...action.payload.offerIds]),
        ];
        return {
          ...state,
          offerMap: {
            ...state.offerMap,
            ...action.payload.offerMap,
          },
          offerList: {
            ...state.offerList,
            offerIds: dedupedOfferIds,
            paginationOptions: {
              ...state.offerList.paginationOptions,
              page: 1,
            },
            count: dedupedOfferIds.length,
            totalPageCount: action.payload.totalPageCount,
            error: null,
            loading: false,
          },
        };
      }
      return {
        ...state,
        offerMap: action.payload.offerMap,
        offerList: {
          offerIds: action.payload.offerIds,
          paginationOptions: {
            ...state.offerList.paginationOptions,
            page: 1,
          },
          count: action.payload.count,
          totalPageCount: action.payload.totalPageCount,
          error: null,
          loading: false,
        },
      };
    case OfferActionTypes.LOAD_OFFERS_FAIL:
      if (action.source) {
        // when source is specified, don't clear existing offers from another source
        return {
          ...state,
          offerList: {
            ...state.offerList,
            error: action.payload,
            loading: false,
          },
        };
      }
      return {
        ...state,
        // clear offer data if it's failed to load new offer data
        offerMap: {},
        offerList: {
          ...state.offerList,
          paginationOptions: {
            ...state.offerList.paginationOptions,
            page: state.offerList.paginationOptions.page - 1,
          },
          offerIds: [],
          error: action.payload,
          count: 0,
          loading: false,
        },
      };
    case OfferActionTypes.VIEW_OFFER_DETAILS:
      return {
        ...state,
        offerDetails: {
          ...state.offerDetails,
          offerId: action.payload,
        },
      };
    case OfferActionTypes.LOAD_SINGLE_OFFER:
      return {
        ...state,
        offerDetails: {
          offerId: action.payload.offerId,
          error: null,
          loading: true,
        },
      };
    case OfferActionTypes.LOAD_SINGLE_OFFER_SUCCESS:
      // Merge single offer with existing offers in the map already
      const newOfferMap = {
        ...state.offerMap,
        ...action.payload,
      };

      return {
        ...state,
        offerMap: newOfferMap,
        offerDetails: {
          ...state.offerDetails,
          loading: false,
        },
      };
    case OfferActionTypes.LOAD_SINGLE_OFFER_FAIL:
      return {
        ...state,
        offerDetails: {
          ...state.offerDetails,
          error: action.payload,
          loading: false,
        },
      };

    case OfferActionTypes.LOAD_MORE_OFFERS:
      return {
        ...state,
        offerList: {
          ...state.offerList,
          paginationOptions: {
            ...state.offerList.paginationOptions,
            page: state.offerList.paginationOptions.page + 1,
          },
          error: null,
          loading: true,
        },
      };
    case OfferActionTypes.LOAD_MORE_OFFERS_SUCCESS:
      return {
        ...state,
        offerMap: {
          ...state.offerMap,
          ...action.payload.offerMap,
        },
        offerList: {
          ...state.offerList,
          offerIds: [...state.offerList.offerIds, ...action.payload.offerIds],
          count: action.payload.count,
          totalPageCount: action.payload.totalPageCount,
          error: null,
          loading: false,
        },
      };
    case OfferActionTypes.RESET_OFFER_LIST:
      return {
        ...state,
        offerList: {
          ...initialState.offerList,
          ...state.offerList.paginationOptions,
        },
      };
  }
  return state;
}
